import { useMemo, useSyncExternalStore } from "react";

import { useAuth } from "@/lib/auth/AuthProvider";

export type FavouriteKind = "screen" | "person";

export interface Favourite {
  /** The address: pathname plus search. No two favourites share one. */
  to: string;
  label: string;
  kind: FavouriteKind;
}

/**
 * The places somebody has pinned to the top of the sidebar.
 *
 * Kept in this browser, under the id of whoever is signed in: a counter
 * computer is shared, and one teller's pins are not the next one's. Every
 * star and the sidebar read the same list from here, so pinning something on
 * a page shows in the sidebar in the same moment. Storage may be blocked or
 * full; then the pins last until the page is closed.
 */
const PREFIX = "hrsys.favourites.";
const NONE: readonly Favourite[] = [];
const listeners = new Set<() => void>();
/** What was last read for each person: the same array until it changes. */
const held = new Map<string, readonly Favourite[]>();

function isFavourite(value: unknown): value is Favourite {
  if (typeof value !== "object" || value === null) return false;
  const one = value as Record<string, unknown>;
  return (
    typeof one.to === "string" &&
    typeof one.label === "string" &&
    (one.kind === "screen" || one.kind === "person")
  );
}

export function readFavourites(userId: string): readonly Favourite[] {
  const known = held.get(userId);
  if (known) return known;
  let list: readonly Favourite[] = NONE;
  try {
    const raw = localStorage.getItem(PREFIX + userId);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (Array.isArray(parsed)) list = parsed.filter(isFavourite);
  } catch {
    // Blocked or damaged: as far as this browser knows, nothing is pinned.
  }
  held.set(userId, list);
  return list;
}

function write(userId: string, next: readonly Favourite[]) {
  held.set(userId, next);
  try {
    localStorage.setItem(PREFIX + userId, JSON.stringify(next));
  } catch {
    // The pins last until the page is closed.
  }
  for (const notify of [...listeners]) notify();
}

/** Another tab pinned or unpinned something: read it again. */
function onStorage(event: StorageEvent) {
  if (event.key !== null && !event.key.startsWith(PREFIX)) return;
  held.clear();
  for (const notify of [...listeners]) notify();
}

function subscribe(notify: () => void) {
  if (listeners.size === 0) window.addEventListener("storage", onStorage);
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

/** Pins a place, at the end of the list, or unpins it if it is there. */
export function toggleFavourite(userId: string, favourite: Favourite) {
  const current = readFavourites(userId);
  write(
    userId,
    current.some((one) => one.to === favourite.to)
      ? current.filter((one) => one.to !== favourite.to)
      : [...current, favourite],
  );
}

export function removeFavourite(userId: string, to: string) {
  const current = readFavourites(userId);
  if (current.some((one) => one.to === to)) {
    write(
      userId,
      current.filter((one) => one.to !== to),
    );
  }
}

/** Moves a favourite one place up (-1) or down (1). The ends stay put. */
export function moveFavourite(userId: string, to: string, by: -1 | 1) {
  const current = readFavourites(userId);
  const from = current.findIndex((one) => one.to === to);
  const target = from + by;
  const moved = current[from];
  const displaced = current[target];
  if (!moved || !displaced) return;
  const next = [...current];
  next[from] = displaced;
  next[target] = moved;
  write(userId, next);
}

/** The signed-in person's favourites, and the ways to change them. */
export function useFavourites() {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  const favourites = useSyncExternalStore(
    subscribe,
    () => (userId ? readFavourites(userId) : NONE),
    () => NONE,
  );
  return useMemo(
    () => ({
      favourites,
      isFavourite: (to: string) => favourites.some((one) => one.to === to),
      toggle: (favourite: Favourite) => {
        if (userId) toggleFavourite(userId, favourite);
      },
      remove: (to: string) => {
        if (userId) removeFavourite(userId, to);
      },
      move: (to: string, by: -1 | 1) => {
        if (userId) moveFavourite(userId, to, by);
      },
    }),
    [favourites, userId],
  );
}
